import React, { useEffect, useRef, useState, useCallback } from 'react';
import { loadMapsLibrary } from '@/lib/googleMaps';
import { SkipForward, Loader2 } from 'lucide-react';

/**
 * Intro cinematografica "Google Earth": volo dalla vista spaziale fino all'area target.
 * Props:
 *   - target: { lat, lng }   (centro area)
 *   - camera: { range, tilt, heading, altitude }  (la "matrice": zoom/scala, inclinazione, orientamento)
 *   - durationMillis (default 3200)
 *   - onComplete: callback a fine volo / skip / errore
 */
const DEFAULT_TARGET = { lat: 39.0846, lng: 16.626 }; // Villaggio Palumbo · Lago Ampollino
// range = distanza camera (m): più piccolo = più vicino. tilt = inclinazione. heading = orientamento.
const DEFAULT_CAMERA = { range: 450, tilt: 62, heading: 30, altitude: 120 };

export const Sila3DIntro = ({
  target = DEFAULT_TARGET,
  camera = DEFAULT_CAMERA,
  durationMillis = 3800,
  onComplete,
}) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const doneRef = useRef(false);
  const [phase, setPhase] = useState('loading'); // loading | flying | fading | error
  const [errMsg, setErrMsg] = useState('');

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setPhase('fading');
    setTimeout(() => onComplete && onComplete(), 750);
  }, [onComplete]);

  useEffect(() => {
    let cancelled = false;
    const node = ref.current;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) { setErrMsg('Chiave Google Maps non configurata'); setPhase('error'); return undefined; }

    // Timeout di sicurezza: se qualcosa si blocca, procedi comunque
    const safety = setTimeout(() => finish(), durationMillis + 4000);

    (async () => {
      try {
        const { Map3DElement } = await loadMapsLibrary('maps3d');
        if (cancelled || !ref.current) return;
        const map = new Map3DElement({
          center: { lat: target.lat, lng: target.lng, altitude: 0 },
          range: 20000000, // vista quasi orbitale (la Terra)
          tilt: 0,
          heading: 0,
        });
        try { map.mode = 'SATELLITE'; } catch (e) { /* noop */ }
        map.style.width = '100%';
        map.style.height = '100%';
        ref.current.appendChild(map);
        mapRef.current = map;
        setPhase('flying');

        // avvia il volo cinematografico
        const start = () => {
          try {
            map.flyCameraTo({
              endCamera: {
                center: { lat: target.lat, lng: target.lng, altitude: camera.altitude ?? 250 },
                range: camera.range ?? 1800,
                tilt: camera.tilt ?? 63,
                heading: camera.heading ?? 25,
              },
              durationMillis,
            });
          } catch (e) { finish(); }
        };
        map.addEventListener('gmp-animationend', () => finish(), { once: true });
        // piccolo delay per far caricare le prime tile
        setTimeout(start, 350);
      } catch (e) {
        if (cancelled) return;
        setErrMsg((e && e.message) || 'Google Photorealistic 3D non disponibile');
        setPhase('error');
        setTimeout(() => finish(), 1800);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(safety);
      try { if (mapRef.current && node?.contains(mapRef.current)) node.removeChild(mapRef.current); } catch (e) { /* noop */ }
    };
  }, [target, camera, durationMillis, finish]);

  return (
    <div
      className="fixed inset-0 z-[300] bg-black transition-opacity duration-700"
      style={{ opacity: phase === 'fading' ? 0 : 1 }}
      data-testid="sila-3d-intro"
    >
      <div ref={ref} className="absolute inset-0" />

      {/* Scrim + testo */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 100%)' }} />
      <div className="pointer-events-none absolute inset-x-0 bottom-10 flex flex-col items-center text-center text-white">
        <p className="font-display text-3xl font-bold drop-shadow-[0_2px_16px_rgba(0,0,0,0.7)] sm:text-5xl">Benvenuto in Sila</p>
        <p className="mt-2 text-sm text-white/80 sm:text-base">Villaggio Palumbo · Lago Ampollino</p>
      </div>

      {/* Stato di caricamento */}
      {phase === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/90">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Preparo il volo sulla Sila…</p>
        </div>
      )}

      {/* Errore -> messaggio breve poi skip */}
      {phase === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-white/90">
          <p className="font-display text-xl font-semibold">Intro 3D non disponibile</p>
          <p className="max-w-md text-sm text-white/70">{errMsg}</p>
        </div>
      )}

      {/* Skip */}
      <button
        type="button"
        onClick={finish}
        data-testid="intro-skip"
        className="absolute right-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md transition-colors hover:bg-white/25"
      >
        Salta<SkipForward className="h-4 w-4" />
      </button>
    </div>
  );
};

export default Sila3DIntro;
